<template>
  <div class="selfscan-root">
    <GlobalNavigation />

    <main class="mb-16 py-8 md:py-12">
      <BaseContainer>
        <div v-if="loading" class="py-24 text-center" aria-live="polite">
          <p class="text-text">{{ $t('selfscan.loading') }}</p>
        </div>

        <div v-else-if="pageError" class="mx-auto max-w-xl py-20 text-center" role="alert">
          <TicketIcon class="mx-auto h-12 w-12 fill-text" />
          <h1 class="mt-5 h2">{{ $t(`selfscan.${pageError}_title`) }}</h1>
          <p class="mt-3 text-text">{{ $t(`selfscan.${pageError}_description`) }}</p>
          <BaseButton class="mt-7" color="muted" to="/">
            <DropdownIcon class="mr-2 h-3 w-3 rotate-90 fill-current" />
            {{ $t('common.back_to_overview') }}
          </BaseButton>
        </div>

        <SelfscanActivationPanel
          v-else-if="flowState !== 'list' && selectedEvent && selectedTicket"
          :state="flowState"
          :event="selectedEvent"
          :ticket="selectedTicket"
          :submitting="activating"
          :error="activationError"
          :countdown-seconds="countdownSeconds"
          :slider-reset-key="sliderResetKey"
          @back="returnToTickets"
          @activate="activateTicket"
        />

        <template v-else-if="order">
          <div class="mb-10 text-center">
            <BaseButton color="muted" to="/">
              <DropdownIcon class="mr-2 h-3 w-3 rotate-90 fill-current" />
              {{ $t('common.back_to_overview') }}
            </BaseButton>
            <h1 class="mt-6 h1-small">{{ $t('selfscan.title') }}</h1>
            <p class="mx-auto mt-3 max-w-2xl text-text">{{ $t('selfscan.description') }}</p>
          </div>

          <div v-if="events.length === 0" class="rounded-md bg-surface-grey-light p-8 text-center text-text">
            {{ $t('selfscan.no_tickets') }}
          </div>
          <SelfscanTicketList
            v-else
            ref="ticketList"
            :events="events"
            @select="selectTicket"
          />
        </template>
      </BaseContainer>
    </main>
  </div>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';

  import { activateSelfscanTicket, fetchSelfscanOrder } from '@/api/selfscan';
  import GlobalNavigation from '@/components/globals/GlobalNavigation.vue';
  import DropdownIcon from '@/components/icons/DropdownIcon.vue';
  import TicketIcon from '@/components/icons/TicketIcon.vue';
  import SelfscanActivationPanel from '@/components/selfscan/SelfscanActivationPanel.vue';
  import SelfscanTicketList, { type SelfscanDisplayEvent } from '@/components/selfscan/SelfscanTicketList.vue';
  import BaseButton from '@/components/ui/BaseButton.vue';
  import BaseContainer from '@/components/ui/BaseContainer.vue';
  import type { SelfscanOrder, SelfscanTicket } from '@/types';

  type PageError = 'invalid_link' | 'not_found';
  type FlowState = 'list' | 'activation' | 'countdown' | 'ticketInfo';
  const COUNTDOWN_DURATION_SECONDS = 30;

  const route = useRoute();
  const { t } = useI18n();
  const order = ref<SelfscanOrder | null>(null);
  const loading = ref(false);
  const pageError = ref<PageError | null>(null);
  const flowState = ref<FlowState>('list');
  const selectedEvent = ref<SelfscanDisplayEvent | null>(null);
  const selectedTicket = ref<SelfscanTicket | null>(null);
  const activating = ref(false);
  const activationError = ref(false);
  const countdownSeconds = ref(COUNTDOWN_DURATION_SECONDS);
  const sliderResetKey = ref(0);
  const ticketList = ref<InstanceType<typeof SelfscanTicketList> | null>(null);
  let loadSequence = 0;
  let countdownDeadline = 0;
  let countdownInterval: number | undefined;

  useHead({
    title: t('selfscan.head_title'),
    meta: [{ name: 'description', content: t('selfscan.description') }],
  });

  const events = computed<SelfscanDisplayEvent[]>(() =>
    (order.value?.events ?? []).map(event => ({
      id: event.id,
      name: event.name,
      eventtime: event.eventtime,
      tickets: Object.values(event.tickets)
        .flatMap(ticketType => Object.values(ticketType))
        .sort((firstTicket, secondTicket) =>
          firstTicket.name1.localeCompare(secondTicket.name1, undefined, { numeric: true }) ||
          firstTicket.ticket_number.localeCompare(secondTicket.ticket_number, undefined, { numeric: true })
        ),
    }))
  );

  watch(
    () => [route.query.id, route.query.mailid],
    () => loadOrder(),
    { immediate: true }
  );

  onBeforeUnmount(clearCountdown);

  async function loadOrder() {
    const sequence = ++loadSequence;
    const orderId = singleQueryValue(route.query.id);
    const mailId = singleQueryValue(route.query.mailid);

    resetView();
    if (!orderId?.match(/^\d+$/) || !mailId?.match(/^[0-9a-f]{96}$/)) {
      pageError.value = 'invalid_link';
      return;
    }

    loading.value = true;
    try {
      const result = await fetchSelfscanOrder(orderId, mailId);
      if (sequence !== loadSequence) return;
      order.value = result;
    } catch {
      if (sequence !== loadSequence) return;
      pageError.value = 'not_found';
    } finally {
      if (sequence === loadSequence) loading.value = false;
    }
  }

  function selectTicket(event: SelfscanDisplayEvent, ticket: SelfscanTicket) {
    if (!ticket.arrived && Date.now() / 1000 + 24 * 60 * 60 <= event.eventtime) return;
    clearCountdown();
    selectedEvent.value = event;
    selectedTicket.value = ticket;
    activationError.value = false;
    flowState.value = ticket.arrived ? 'ticketInfo' : 'activation';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function activateTicket() {
    const orderId = singleQueryValue(route.query.id);
    const mailId = singleQueryValue(route.query.mailid);
    const ticket = selectedTicket.value;
    if (activating.value || !orderId || !mailId || !ticket) return;

    activating.value = true;
    activationError.value = false;
    try {
      const result = await activateSelfscanTicket(orderId, mailId, ticket.ticket_number);
      if (result.ticket_number !== ticket.ticket_number || result.arrived <= 0) {
        throw new Error('Selfscan activation response did not match the selected ticket');
      }
      ticket.arrived = result.arrived;
      startCountdown();
    } catch {
      activationError.value = true;
      sliderResetKey.value += 1;
    } finally {
      activating.value = false;
    }
  }

  async function returnToTickets() {
    if (activating.value) return;
    const ticketNumber = selectedTicket.value?.ticket_number;
    clearCountdown();
    flowState.value = 'list';
    selectedEvent.value = null;
    selectedTicket.value = null;
    activationError.value = false;
    await nextTick();
    if (ticketNumber) ticketList.value?.focusTicket(ticketNumber);
  }

  function resetView() {
    clearCountdown();
    flowState.value = 'list';
    order.value = null;
    pageError.value = null;
    selectedEvent.value = null;
    selectedTicket.value = null;
    activationError.value = false;
    countdownSeconds.value = COUNTDOWN_DURATION_SECONDS;
  }

  function startCountdown() {
    clearCountdown();
    flowState.value = 'countdown';
    countdownSeconds.value = COUNTDOWN_DURATION_SECONDS;
    countdownDeadline = Date.now() + COUNTDOWN_DURATION_SECONDS * 1000;
    countdownInterval = window.setInterval(updateCountdown, 250);
  }

  function updateCountdown() {
    countdownSeconds.value = Math.max(Math.ceil((countdownDeadline - Date.now()) / 1000), 0);
    if (countdownSeconds.value === 0) {
      clearCountdown();
      flowState.value = 'ticketInfo';
    }
  }

  function clearCountdown() {
    if (countdownInterval !== undefined) {
      window.clearInterval(countdownInterval);
      countdownInterval = undefined;
    }
    countdownDeadline = 0;
  }

  function singleQueryValue(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }
</script>